import React, { useEffect, useState } from 'react';
import { useModal } from '../Modal';
import { Button, IconButton, Tooltip } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { Clear } from "@mui/icons-material";
import WizardSearchStep2 from "./WizardSearchStep2";
import WizardSearchStep1 from "./WizardSearchStep1";
import Events from "../../../events";
import { EventsType } from "../../../events-types";
import { getDynamicCollectionsRegistry } from '../../../api/DynamicCollections';
import { useMessage } from '../../ContextMessage';

interface WizardSearchProps {
    data: Record<string, any>;
}

const WizardSearch: React.FC<WizardSearchProps> = ({ data }) => {
    const { isOpen, handleOpenModal, handleCloseModal } = useModal();
    const { showWarning } = useMessage();

    const [currentStep, setCurrentStep] = useState(1);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [selectedObject, setSelectedObject] = useState<any>(null);
    const [propertyValues, setPropertyValues] = useState<Array<{ property: string; value: string | number; operator: string }>>([]);
    const [dynamicCollections, setDynamicCollections] = useState<any[]>([]);
    const [isDynamic, setIsDynamic] = useState<boolean>(false);

    // Fetch dynamic collections on mount
    useEffect(() => {
        const fetchDynamicCollections = async () => {
            try {
                const collections = await getDynamicCollectionsRegistry();
                setDynamicCollections(collections);
            } catch (error) {
                console.error('Failed to fetch dynamic collections:', error);
                showWarning('Failed to load dynamic collections.');
            }
        };
        fetchDynamicCollections();
    }, [showWarning]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCloseModal();
            }
        };

        handleAddProperty();

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        } else {
            window.removeEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    const handleNextStep = () => {
        setCurrentStep(currentStep + 1);
    };

    const handleObjectSelect = (key: string, isDynamicCollection: boolean = false) => {
        setSelectedKey(key);
        setIsDynamic(isDynamicCollection);
        const selectedData = isDynamicCollection
            ? dynamicCollections.find(col => col.collection_name === key)
            : data[key || ''];
        setSelectedObject(selectedData || {});
    };

    const handlePropertyValueChange = (index: number, updatedProperty: { property: string; value: string | number; operator: string }) => {
        setPropertyValues((prevPropertyValues) => {
            const updatedPropertyValues = [...prevPropertyValues];
            const updatedValue = updatedProperty.value;

            if (typeof updatedValue === 'string' && !isNaN(Number(updatedValue))) {
                updatedProperty.value = Number(updatedValue);
            }

            updatedPropertyValues[index] = updatedProperty;
            return updatedPropertyValues;
        });
    };

    const handleAddProperty = () => {
        setPropertyValues((prevPropertyValues) => [
            ...prevPropertyValues,
            { property: '', value: '', operator: '==' },
        ]);
    };

    const handleExecute = () => {
        if (isDynamic) {
            Events.trigger(EventsType.SEARCH_DYNAMIC, {
                collectionName: selectedKey,
                filters: propertyValues,
            });
        } else {
            Events.trigger(EventsType.SEARCH_WIZARD, {
                component: selectedKey,
                filters: propertyValues,
            });
        }
        handleCloseModal();
    };

    function reset() {
        setPropertyValues([]);
        setSelectedObject(null);
        setSelectedKey(null);
        setIsDynamic(false);
        setCurrentStep(1);
    }

    return (
        <div>
            <Tooltip title="Search data using Wizard" placement="top" arrow>
                <Button
                    style={{ height: '32px', boxShadow: '0px 0px 0px 0px', marginRight: '5px' }}
                    variant="contained"
                    color="primary"
                    onClick={() => { reset(); handleOpenModal({}); }}
                    aria-label="Open Wizard"
                >
                    <AutoFixHighIcon />
                </Button>
            </Tooltip>
            {isOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="card-edit">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: '500px' }}>
                                <h2 style={{ width: '90%' }}>Advanced Search Wizard</h2>
                                <IconButton
                                    style={{ width: '30px' }}
                                    onClick={handleCloseModal}
                                    color="primary"
                                >
                                    <Clear />
                                </IconButton>
                            </div>

                            {currentStep === 1 && (
                                <WizardSearchStep1
                                    onSelect={handleObjectSelect}
                                    onNext={handleNextStep}
                                    data={data}
                                    dynamicCollections={dynamicCollections}
                                />
                            )}
                            {currentStep === 2 && (
                                <WizardSearchStep2
                                    selectedKey={selectedKey}
                                    selectedObject={selectedObject}
                                    onNext={handleExecute}
                                    onAddProperty={handleAddProperty}
                                    propertyValues={propertyValues}
                                    onPropertyValueChange={handlePropertyValueChange}
                                    isDynamic={isDynamic}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WizardSearch;
